/**
 * ratingDwell: a look, then a pause (owner direction, 2026-10-26).
 *
 * A success (a scan that added entries, a collection milestone) does not ask
 * for the store rating on the spot. The sheet is modal, and the moment after
 * a scan is exactly when the trainer wants to see what it did: the grid
 * filling in, the Pokémon it saved. So a success ARMS a pending moment here,
 * and the moment fires only once the trainer has had a look:
 *
 *   - they are on the results surface (the dex grid for a Pokédex scan, My
 *     Pokémon for a scan of individual Pokémon pages; a milestone may fire
 *     on any page), with the app in the foreground;
 *   - and they have either scrolled and then held still for RATING_PAUSE_MS
 *     (they looked, and stopped), or spent RATING_SETTLED_MS there without
 *     scrolling (the new entries were already on screen).
 *
 * The moment is DROPPED, not delayed, when they leave the results surface
 * before that (they were done; a sheet landing on the next page they wanted
 * is an interruption again), when the app goes to the background, when
 * another success replaces it, or after RATING_PENDING_MAX_MS. The next
 * success starts over. Whether the sheet may show at all is still
 * lib/firstRunPrompts' call (one ask a session, the floors, the repeat).
 *
 * Only user scrolls count: components/RatingMoments listens for touchmove and
 * wheel, not the scroll event, which the page's own scroll restoration fires.
 *
 * Pure module state so it can be driven from one place and unit-tested
 * (ratingDwell.test.ts); the driver is components/RatingMoments, the pages
 * report themselves with lib/useRatingSurface.
 *
 * Every arm, fire and drop logs one line with its reason (lib/ratingLog), so
 * a device report can say whether the look happened and, if not, why the
 * moment went: left the results, backgrounded, aged out, replaced, reset.
 */
import type { RatingMoment } from "./storeReview";
import { ratingLog } from "./ratingLog";

export type RatingSurfaceId = "dex" | "my-pokemon";
/** Where the moment may fire: a results surface, or anywhere. */
export type RatingSurface = RatingSurfaceId | "any";

/** After a scroll, this long still means they have looked and stopped. */
export const RATING_PAUSE_MS = 3_000;
/** Without a scroll, this long on the surface means the result was in view. */
export const RATING_SETTLED_MS = 30_000;
/** A moment older than this is history, not a moment. */
export const RATING_PENDING_MAX_MS = 5 * 60_000;
/**
 * Leaving the surface drops the moment only once it has stayed gone this
 * long: the My Pokémon list and a Pokémon's own page are one surface, and
 * the hop between them unmounts one before it mounts the other.
 */
export const RATING_LEAVE_GRACE_MS = 1_500;
/** How often the driver asks ratingMomentDue. */
export const RATING_TICK_MS = 1_000;

export interface PendingRatingMoment {
  moment: RatingMoment;
  surface: RatingSurface;
  armedAt: number;
}

/** How a look was recognised, for the log line when the moment fires. */
export type RatingLook = "pause" | "settled";

/** Why a pending moment was dropped rather than fired. */
export type RatingDropReason = "left" | "hidden" | "aged" | "reset";

interface DwellState {
  pending: PendingRatingMoment | null;
  /** The results surface on screen, and since when. */
  surface: { id: RatingSurfaceId; since: number } | null;
  /** The results surface last left since arming, for the leave grace. */
  leftAt: { id: RatingSurfaceId; at: number } | null;
  /** The last user scroll since arming. */
  lastScrollAt: number | null;
  /** Set by ratingMomentDue when it says yes: which look it saw. */
  look: RatingLook | null;
}

const EMPTY: DwellState = { pending: null, surface: null, leftAt: null, lastScrollAt: null, look: null };
let state: DwellState = { ...EMPTY };

/** The surface in words, for the log. */
export function ratingSurfaceLabel(surface: RatingSurface): string {
  if (surface === "dex") return "the dex grid";
  if (surface === "my-pokemon") return "My Pokémon";
  return "any page";
}

function clearPending(): void {
  state.pending = null;
  state.lastScrollAt = null;
  state.leftAt = null;
  state.look = null;
}

/** A success happened: wait for the look. Replaces any earlier moment. */
export function armRatingMoment(moment: RatingMoment, surface: RatingSurface, now: number = Date.now()): void {
  const replaced = state.pending;
  clearPending();
  state.pending = { moment, surface, armedAt: now };
  ratingLog(
    `armed at ${moment}: waits for a look on ${ratingSurfaceLabel(surface)}` +
      (replaced ? ` (replaces the ${replaced.moment} moment)` : ""),
  );
}

/**
 * Forget the pending moment: the app hid, they left, it aged out, an admin
 * reset it. Logs the reason when there was one to drop.
 */
export function dropRatingMoment(reason: RatingDropReason = "hidden"): void {
  const p = state.pending;
  clearPending();
  if (!p) return;
  ratingLog(`dropped the ${p.moment} moment: ${describeDrop(reason, p)}`);
}

function describeDrop(reason: RatingDropReason, p: PendingRatingMoment): string {
  switch (reason) {
    case "left":
      return `left ${ratingSurfaceLabel(p.surface)} before the look`;
    case "hidden":
      return "app went to the background";
    case "aged":
      return state.surface?.id === p.surface || p.surface === "any"
        ? `aged out after ${RATING_PENDING_MAX_MS / 60_000} min on ${ratingSurfaceLabel(p.surface)} without a look`
        : `aged out after ${RATING_PENDING_MAX_MS / 60_000} min, never on ${ratingSurfaceLabel(p.surface)}`;
    case "reset":
      return "reset from Settings";
  }
}

export function pendingRatingMoment(): PendingRatingMoment | null {
  return state.pending;
}

/** A results surface is on screen. */
export function enterRatingSurface(id: RatingSurfaceId, now: number = Date.now()): void {
  state.surface = { id, since: now };
  if (state.leftAt?.id === id) state.leftAt = null;
}

/** A results surface went away (unmounted). */
export function leaveRatingSurface(id: RatingSurfaceId, now: number = Date.now()): void {
  if (state.surface?.id !== id) return;
  state.surface = null;
  if (state.pending) state.leftAt = { id, at: now };
}

/**
 * The trainer scrolled (a finger or a wheel, never the page's own scroll).
 * Counts only where the look must happen: on the results surface, or
 * anywhere for a moment that may fire on any page.
 */
export function noteRatingScroll(now: number = Date.now()): void {
  const p = state.pending;
  if (!p) return;
  if (p.surface !== "any" && state.surface?.id !== p.surface) return;
  state.lastScrollAt = now;
}

/**
 * Has the look happened? Also where a stale or abandoned moment is dropped,
 * so the driver only ever asks this and takeRatingMoment.
 */
export function ratingMomentDue(now: number = Date.now()): boolean {
  const p = state.pending;
  if (!p) return false;
  if (now - p.armedAt > RATING_PENDING_MAX_MS) {
    dropRatingMoment("aged");
    return false;
  }
  let since = p.armedAt;
  if (p.surface !== "any") {
    if (state.surface?.id !== p.surface) {
      // Off the results. Left them after having been there: they were done.
      if (state.leftAt?.id === p.surface && now - state.leftAt.at >= RATING_LEAVE_GRACE_MS) dropRatingMoment("left");
      return false;
    }
    since = Math.max(since, state.surface.since);
  }
  if (state.lastScrollAt != null) {
    const due = now - state.lastScrollAt >= RATING_PAUSE_MS;
    state.look = due ? "pause" : null;
    return due;
  }
  const due = now - since >= RATING_SETTLED_MS;
  state.look = due ? "settled" : null;
  return due;
}

/** The moment, once due: hand it over and clear it. */
export function takeRatingMoment(): PendingRatingMoment | null {
  const p = state.pending;
  const look = state.look;
  clearPending();
  if (p) {
    ratingLog(
      `look done at ${p.moment}: ` +
        (look === "pause"
          ? `scrolled, then ${RATING_PAUSE_MS / 1000} s still`
          : look === "settled"
            ? `${RATING_SETTLED_MS / 1000} s settled on ${ratingSurfaceLabel(p.surface)}`
            : "taken before it was due"),
    );
  }
  return p;
}

/** Tests only. */
export function __resetRatingDwellForTest(): void {
  state = { ...EMPTY };
}
